import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { SEGMENTED_TABLIST_CLASS } from './tabStyles.js'

interface TabStripProps {
  /** The tab buttons, each `role="tab"` with `aria-selected`. */
  children: ReactNode
  /** Changes when the chosen tab does, so the strip brings it into view. */
  activeKey: string
  /** Space around the strip; the frame itself is `SEGMENTED_TABLIST_CLASS`. */
  className?: string
}

/** How far a chevron press moves the row: most of what is showing, so the reader keeps their place. */
const PAGE_FRACTION = 0.8
/** Slack before an edge counts as reached, for sub-pixel widths. */
const EDGE_SLACK = 2

/**
 * The nested tab strip as one row that never wraps: when the tabs are wider
 * than the room they have, the row scrolls sideways (wheel, trackpad, touch),
 * the clipped edge fades and a chevron on that side pages along, and the
 * chosen tab is always brought into view.
 */
export function TabStrip({ children, activeKey, className = '' }: TabStripProps) {
  const scroller = useRef<HTMLDivElement>(null)
  const [overflow, setOverflow] = useState({ start: false, end: false })

  const measure = useCallback((): void => {
    const element = scroller.current
    if (!element) {
      return
    }
    const start = element.scrollLeft > EDGE_SLACK
    const end = element.scrollLeft + element.clientWidth < element.scrollWidth - EDGE_SLACK
    setOverflow((current) => (current.start === start && current.end === end ? current : { start, end }))
  }, [])

  useLayoutEffect(() => {
    const element = scroller.current
    if (!element) {
      return
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    Array.from(element.children).forEach((child) => observer.observe(child))
    return () => observer.disconnect()
  }, [measure, children])

  // Scrolls the row only, never the page, so choosing a tab does not jump the screen.
  useEffect(() => {
    const element = scroller.current
    const chosen = element?.querySelector<HTMLElement>('[aria-selected="true"]')
    if (!element || !chosen) {
      return
    }
    // The row is the tab's offset parent (it is positioned), so offsetLeft is already measured from its start.
    const left = chosen.offsetLeft
    const right = left + chosen.offsetWidth
    if (left < element.scrollLeft) {
      element.scrollTo({ left: left - 24, behavior: 'smooth' })
    } else if (right > element.scrollLeft + element.clientWidth) {
      element.scrollTo({ left: right - element.clientWidth + 24, behavior: 'smooth' })
    }
  }, [activeKey])

  const page = (direction: 1 | -1): void => {
    const element = scroller.current
    element?.scrollBy({ left: direction * element.clientWidth * PAGE_FRACTION, behavior: 'smooth' })
  }

  return (
    <div className={`relative flex max-w-full ${className}`}>
      <div className={`${SEGMENTED_TABLIST_CLASS} relative min-w-0 overflow-hidden`}>
        <div
          ref={scroller}
          role="tablist"
          onScroll={measure}
          className="relative flex min-w-0 gap-1 overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {children}
        </div>
        {overflow.start && <EdgeButton side="start" onPress={() => page(-1)} />}
        {overflow.end && <EdgeButton side="end" onPress={() => page(1)} />}
      </div>
    </div>
  )
}

function EdgeButton({ side, onPress }: { side: 'start' | 'end'; onPress: () => void }) {
  const Icon = side === 'start' ? ChevronLeft : ChevronRight
  const position = side === 'start' ? 'left-0 pl-1 pr-6 bg-gradient-to-r' : 'right-0 pr-1 pl-6 bg-gradient-to-l justify-end'
  return (
    // The fade is the button's own background, so the hidden tabs read as more to come rather than cut off.
    <div className={`pointer-events-none absolute inset-y-0 flex items-center from-white via-white/90 to-transparent ${position}`}>
      <button
        type="button"
        tabIndex={-1}
        aria-label={side === 'start' ? 'Show earlier tabs' : 'Show more tabs'}
        onClick={onPress}
        className="pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full border border-marine/20 bg-white text-marine shadow-sm hover:border-marine hover:text-marine-dk"
      >
        <Icon className="h-4 w-4" aria-hidden />
      </button>
    </div>
  )
}
